/* global grecaptcha */
import { on } from '@src/utils';

class Newsletter {
  constructor() {
    this.listeners = [];

    /** @type {HTMLFormElement[]} */
    this.forms = Array.from(document.querySelectorAll('form.js-newsletter-form'));

    this.forms.forEach(form => {
      this.initForm(form);
    });
  }

  /**
   * Initializes a newsletter form. Adds a `submit` listener.
   *
   * @param {HTMLFormElement} form the form element
   */
  initForm(form) {
    const off = on(form, 'submit', this.onFormSubmit.bind(this));
    this.listeners.push(off);
  }

  /**
   * When the form has been submitted successfully, set the `data-submitted`
   * attribute on all forms to disable additional submits.
   */
  onSuccess() {
    this.forms.forEach(form => {
      form.dataset.submitted = true;
    });
  }

  /**
   * Updates the submitting state of all forms when the user has started a
   * form submission. Sets the `data-submitting` attribute.
   *
   * @param {boolean} state the current submission state
   */
  onSubmitting(state) {
    this.forms.forEach(form => {
      form.dataset.submitting = state;
    });
  }

  /**
   * Shows an alert for the form. Uses aria-* attributes similarly to
   * AMP functionality.
   *
   * @param {HTMLElement} alertEl the element to display
   */
  showAlert(alertEl) {
    alertEl.setAttribute('aria-role', 'alert');
    alertEl.setAttribute('aria-live', 'assertive');
  }

  /**
   * Shows an alert from the form. Uses aria-* attributes similarly to
   * AMP functionality.
   *
   * @param {HTMLElement} alertEl the element to hide
   */
  hideAlert(alertEl) {
    alertEl.removeAttribute('aria-role');
    alertEl.removeAttribute('aria-live');
  }

  /**
   * Handler for a newsletter form submit. Sends a request to the REST API to subscribe a user to
   * a newsletter. This toggles the submitting, success, and error alerts on the form appropriately.
   *
   * There are a few caveats where the form won't submit:
   * - If the form is currently submitting (as denoted by the `data-submitting` attribute), the
   * form won't be submitted again.
   * - If the form is disabled (as denoted by the `data-disabled` attribute), the form will
   * automatically show a success message without contacting the API.
   *
   * @param {InputEvent} evt the form submit event
   */
  async onFormSubmit(evt) {
    evt.preventDefault();

    /** @type {HTMLFormElement} */
    const form = evt.target;

    this.form = form;

    const captchaEl = form.querySelector('.newsletter-captcha');
    const termsEl = form.querySelector('.newsletter-email-and-terms');

    captchaEl.classList.remove('newsletter-hidden');
    termsEl.classList.add('newsletter-hidden');

    // If form is currently submitting, return early
    if (form.dataset.submitting === 'true') {
      return;
    }

    // Get alert elements
    const submittingAlertEl = form.querySelector('[submitting]');
    const successAlertEl = form.querySelector('[submit-success]');
    const errorAlertEl = form.querySelector('[submit-error]');
    const undeliverableAlertEl = form.querySelector('[submit-undeliverable-error]');

    // Reset alert visibility
    this.hideAlert(submittingAlertEl);
    this.hideAlert(successAlertEl);
    this.hideAlert(errorAlertEl);
    this.hideAlert(undeliverableAlertEl);

    if (form.dataset.disabled) {
      // If disabled, just auto-set to success
      this.onSubmitting(false);
      this.onSuccess();
      return;
    }

    // Now we wait for the user to solve the CAPTCHA. We don't need to do
    // anything until that resolves.  When they solve it, it will call
    // recaptchaSolvedCallback.
  }

  /**
   * Submits the data for the form to the `/validate-and-subscribe` endpoint.
   * It manages the state of the form, to put success or error messages.
   * 
   * This will be called by recaptcha, which will pass the token for us to
   * verify.  Recaptcha knows to call this because it was placed in the
   * data-callback section of the recaptcha div (in newsletter.twig).
   * Actually, recaptcha needs the name of a globally accessible function, so
   * we instantiate the Newsletter, then use bind to create an anonymous
   * function that calls this callback, and then set that to
   * window.newsletterRecaptchaCallback.  We do this in app.js.
   *
   */
  async recaptchaSolvedCallback(token) {
    const submittingAlertEl = this.form.querySelector('[submitting]');
    const successAlertEl = this.form.querySelector('[submit-success]');
    const errorAlertEl = this.form.querySelector('[submit-error]');
    const undeliverableAlertEl = this.form.querySelector('[submit-undeliverable-error]');

    // Show loading alert
    this.onSubmitting(true);
    document.querySelectorAll('.newsletter-form .newsletter-captcha')[0].classList.add('newsletter-hidden');
    this.showAlert(submittingAlertEl);

    // Fetch from API using form data
    // We don't have to explicitly place the token into the form, because
    // recaptcha has placed it into a hidden field for us already.
    const response = await fetch('/wp-json/newsletter/v1/validate-and-subscribe', {
      method: 'POST',
      body: new FormData(this.form),
    });

    const wasSuccess = response.ok;
    const data = await response.json();

    // Hide loading alert
    this.onSubmitting(false);
    this.hideAlert(submittingAlertEl);

    // Show either success of error alert
    if (data.code == 'newsletter_undeliverable') {
      this.showAlert(undeliverableAlertEl);
    } else if (wasSuccess) {
      this.showAlert(successAlertEl);
      this.onSuccess();
    } else {
      this.showAlert(errorAlertEl);
    }
  }
}

export default Newsletter;
